import { useState, useEffect } from "react";
import { Globe, Eye, Inbox, ExternalLink, FileText, Loader2, Layers } from "lucide-react";
import api from "@/lib/api";
import { money } from "@/lib/landingSim";
import { InvoiceStatus } from "@/components/webadvisor/InvoiceTable";

const Stat = ({ icon: Icon, label, value, testId }) => (
  <div className="bg-slate-50 border border-slate-200 rounded-lg p-3">
    <p className="text-[10px] uppercase font-bold text-slate-400 flex items-center gap-1"><Icon className="w-3 h-3" />{label}</p>
    <p className="text-xl font-black text-slate-900" data-testid={testId}>{value}</p>
  </div>
);

const Billing = ({ b }) => (
  <div className="flex flex-wrap items-center justify-between gap-3 bg-blue-50 border border-blue-100 rounded-lg p-3" data-testid="my-pages-billing">
    <div className="text-sm">
      <p className="font-semibold text-slate-900 flex items-center gap-2"><FileText className="w-4 h-4 text-blue-600" />Invoice {b.invoice_number} <InvoiceStatus status={b.status} /></p>
      <p className="text-slate-600">Total {money(b.total)} · Paid {money(b.paid)} · <b>Balance {money(b.balance)}</b></p>
    </div>
    <div className="flex flex-wrap gap-2">
      <a href={`/invoice/${b.share_token}`} className="bg-blue-600 hover:bg-blue-500 text-white text-sm font-bold px-4 py-2 rounded-lg" data-testid="my-pages-open-invoice">View invoice</a>
      <a href={`/p/${b.share_token}#${b.packet_ready ? "packet" : "respond"}`} className="border border-blue-300 text-blue-700 hover:bg-blue-50 text-sm font-bold px-4 py-2 rounded-lg" data-testid="my-pages-open-packet">{b.packet_ready ? "Plan & contract" : "View proposal"}</a>
    </div>
  </div>
);

const Site = ({ s }) => (
  <div className="bg-white border border-slate-200 rounded-xl p-5 shadow-sm space-y-4" data-testid={`my-site-${s.audit_id}`}>
    <div><h3 className="text-lg font-bold text-slate-900">{s.business_name}</h3><a href={s.website} target="_blank" rel="noreferrer" className="text-xs text-slate-400 hover:underline break-all">{s.website}</a></div>
    <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
      <Stat icon={Layers} label="Pages" value={s.totals.pages} testId="my-pages-total" />
      <Stat icon={Globe} label="Live" value={s.totals.live} testId="my-pages-live" />
      <Stat icon={Eye} label="Views" value={s.totals.views.toLocaleString()} testId="my-pages-views" />
      <Stat icon={Inbox} label="Leads" value={s.totals.leads} testId="my-pages-leads" />
    </div>
    {s.billing && <Billing b={s.billing} />}
    {s.pages.length === 0 && <p className="text-sm text-slate-500 bg-slate-50 border border-dashed border-slate-200 rounded-lg p-3" data-testid="my-pages-coming-soon">Your landing pages are being prepared — they'll appear here with live views and leads as soon as they're built.</p>}
    {s.pages.length > 0 && (
      <div className="overflow-x-auto max-h-[380px] overflow-y-auto">
        <table className="w-full text-left text-sm">
          <thead className="text-[10px] uppercase text-slate-400 sticky top-0 bg-white"><tr><th className="py-2">Page</th><th>Status</th><th className="text-right">Views</th><th className="text-right">Leads</th></tr></thead>
          <tbody>{s.pages.map(p => (
            <tr key={p.id} className="border-b border-slate-100" data-testid={`my-page-row-${p.slug}`}>
              <td className="py-2 pr-3">
                {p.status === "live" ? <a href={`/lp/${s.site_slug}/${p.slug}`} target="_blank" rel="noreferrer" className="font-medium text-blue-700 hover:underline flex items-center gap-1">{p.title}<ExternalLink className="w-3 h-3" /></a> : <span className="font-medium text-slate-700">{p.title}</span>}
                <p className="text-xs text-slate-400">{p.primary_keyword}</p>
              </td>
              <td><span className={`text-[11px] font-bold px-2 py-0.5 rounded-full ${p.status === "live" ? "bg-green-100 text-green-700" : "bg-slate-100 text-slate-500"}`}>{p.status === "live" ? "Live" : "In review"}</span></td>
              <td className="text-right">{p.views}</td><td className="text-right">{p.leads}</td>
            </tr>
          ))}</tbody>
        </table>
      </div>
    )}
    <div>
      <p className="font-semibold text-slate-900 mb-2">Leads from your pages</p>
      {s.enquiries.length === 0 ? <p className="text-sm text-slate-400" data-testid="my-leads-empty">No leads yet — they'll show up here as soon as visitors reach out.</p> : (
        <div className="space-y-2" data-testid="my-leads-list">{s.enquiries.map(q => (
          <div key={q.id} className="border border-slate-200 rounded-lg p-3 text-sm">
            <div className="flex flex-wrap justify-between gap-2"><p className="font-semibold text-slate-900">{q.name}</p><p className="text-xs text-slate-400">{q.created_at.slice(0, 10)} · {q.page_title}</p></div>
            <p className="text-slate-600">{[q.phone, q.email].filter(Boolean).join(" · ")}</p>
            {q.message && <p className="text-slate-500 italic mt-1">“{q.message}”</p>}
          </div>
        ))}</div>
      )}
    </div>
  </div>
);

export const MyPagesTab = () => {
  const [sites, setSites] = useState(null);
  useEffect(() => { api.get("/api/customer/webadvisor").then(r => setSites(r.data.sites)).catch(() => setSites([])); }, []);
  if (!sites) return <Loader2 className="w-6 h-6 animate-spin text-blue-600 mx-auto my-10" />;
  if (!sites.length) return (
    <div className="bg-white border border-slate-200 rounded-xl p-8 text-center text-slate-500" data-testid="my-pages-empty">
      <Layers className="w-8 h-8 mx-auto text-slate-300 mb-2" />No landing pages yet. Once your advisor builds your pages, you'll see them here with views and leads.
    </div>
  );
  return <div className="space-y-5" data-testid="my-pages-tab">{sites.map(s => <Site key={s.audit_id} s={s} />)}</div>;
};
